/**
 * Domain form primitives (F1): create/edit dialog and compact field rows, so
 * each page only declares its own fields.
 * F3 adds `UploadField`: REAL upload to the platform storage
 * (`uploadStorageObject`, with the `X-Active-Account` selector).
 */
import { createSignal, type JSX, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label, TextField } from "@fleet/ui/text-field";
import { showToast } from "@/components/ui/toast";
import { uploadStorageObject } from "@/lib/api";
import { useI18n } from "@/providers/i18n-provider";

interface FormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  icon?: JSX.Element;
  submitLabel?: string;
  submitTestId?: string;
  disabled?: boolean;
  onSubmit: () => void | Promise<void>;
  children: JSX.Element;
}

export function FormDialog(props: FormDialogProps) {
  const { t } = useI18n();
  const handleSubmit = (e: Event) => {
    e.preventDefault();
    // Now that `patchRowVerified` can REJECT (the row did not land in the
    // PowerSync view after retrying), a bare `void` would leave that rejection
    // as an unhandled rejection instead of telling the user the change was
    // not saved.
    Promise.resolve(props.onSubmit()).catch(() => {
      showToast(t(msg`Couldn't save the change — try again`), "error");
    });
  };
  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      role="dialog"
      contentClass="max-w-lg p-0"
    >
      <DialogHeader size="md">
        <div class="flex items-center gap-3">
          <Show when={props.icon}>
            <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
              {props.icon}
            </div>
          </Show>
          <DialogTitle class="text-xl font-bold">{props.title}</DialogTitle>
        </div>
      </DialogHeader>
      <form onSubmit={handleSubmit} class="max-h-[70vh] space-y-4 overflow-y-auto p-6">
        {props.children}
        <div class="flex items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              props.onOpenChange(false);
            }}
          >
            {t(msg`Cancel`)}
          </Button>
          <Button type="submit" disabled={props.disabled} data-testid={props.submitTestId}>
            {props.submitLabel ?? t(msg`Save`)}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

interface FieldProps {
  id: string;
  label: string;
  children?: JSX.Element;
  value?: string;
  onInput?: (value: string) => void;
  type?: string;
  placeholder?: string;
  testId?: string;
}

/** Label+control row. Without `children`, it renders a controlled TextField. */
export function Field(props: FieldProps) {
  return (
    <div class="space-y-1.5">
      <Label for={props.id} class="text-xs font-bold uppercase tracking-wider opacity-60">
        {props.label}
      </Label>
      <Show
        when={props.children}
        fallback={
          <TextField
            id={props.id}
            type={props.type ?? "text"}
            value={props.value ?? ""}
            placeholder={props.placeholder}
            data-testid={props.testId}
            onInput={(e) => props.onInput?.(e.currentTarget.value)}
          />
        }
      >
        {props.children}
      </Show>
    </div>
  );
}

/**
 * File upload to the real storage (F3): uploads with `uploadStorageObject` and
 * hands the resulting URL to the caller (which saves it in its local-first row).
 */
export function UploadField(props: {
  id: string;
  label: string;
  accept?: string;
  testId?: string;
  onUploaded: (url: string) => void;
}) {
  const { t } = useI18n();
  const [uploading, setUploading] = createSignal(false);
  const handleChange = async (file: File | undefined) => {
    if (!file) {
      return;
    }
    setUploading(true);
    try {
      const result = await uploadStorageObject(file);
      props.onUploaded(result.url);
    } catch {
      showToast(t(msg`Upload failed`), "error");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div class="space-y-1.5">
      <Label for={props.id} class="text-xs font-bold uppercase tracking-wider opacity-60">
        {props.label}
      </Label>
      <div class="flex items-center gap-2">
        <input
          id={props.id}
          type="file"
          accept={props.accept ?? "image/*"}
          class="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-accent file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-accent-foreground hover:file:bg-accent/80"
          data-testid={props.testId}
          onChange={(e) => void handleChange(e.currentTarget.files?.[0])}
        />
        <Show when={uploading()}>
          <span class="shrink-0 text-xs text-muted-foreground">{t(msg`Uploading…`)}</span>
        </Show>
      </div>
    </div>
  );
}

/** Controlled multiline text area, same style as TextField. */
export function TextArea(props: {
  id: string;
  value: string;
  onInput: (value: string) => void;
  rows?: number;
  placeholder?: string;
  class?: string;
  testId?: string;
}) {
  return (
    <textarea
      id={props.id}
      rows={props.rows ?? 4}
      value={props.value}
      placeholder={props.placeholder}
      data-testid={props.testId}
      onInput={(e) => props.onInput(e.currentTarget.value)}
      class={`flex w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${props.class ?? ""}`}
    />
  );
}
